import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { LogIn, UserPlus, KeyRound, Loader2, AlertCircle, Ban, CheckCircle2 } from 'lucide-react';
import { scorePassword } from '@/lib/passwordStrength';
import { Link } from 'react-router-dom';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import { TurnstileWidget, TURNSTILE_ENABLED } from '@/components/auth/TurnstileWidget';
import {
  getLockoutSecondsLeft,
  recordSignInFailure,
  clearSignInThrottle,
} from '@/lib/bruteForceGuard';

interface AuthDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Optional preselected tab. */
  defaultTab?: 'signin' | 'signup' | 'forgot';
}

type Tab = 'signin' | 'signup' | 'forgot';

export function AuthDialog({ open, onOpenChange, defaultTab = 'signin' }: AuthDialogProps) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [tab, setTab] = useState<Tab>(defaultTab);

  // Sign-in state
  const [siEmail, setSiEmail] = useState('');
  const [siPassword, setSiPassword] = useState('');
  const [siLoading, setSiLoading] = useState(false);
  const [siError, setSiError] = useState<string | null>(null);
  const [banReason, setBanReason] = useState<string | null>(null);

  // Sign-up state
  const [suEmail, setSuEmail] = useState('');
  const [suPassword, setSuPassword] = useState('');
  const [suConfirm, setSuConfirm] = useState('');
  const [suAccept, setSuAccept] = useState(false);
  const [suCaptchaToken, setSuCaptchaToken] = useState<string | null>(null);
  const [suLoading, setSuLoading] = useState(false);
  const [suError, setSuError] = useState<string | null>(null);
  const [suSuccess, setSuSuccess] = useState(false);

  // Forgot-password state
  const [fpEmail, setFpEmail] = useState('');
  const [fpLoading, setFpLoading] = useState(false);
  const [fpError, setFpError] = useState<string | null>(null);
  const [fpSuccess, setFpSuccess] = useState(false);

  // Reset all transient state when dialog closes.
  useEffect(() => {
    if (!open) {
      setTab(defaultTab);
      setSiEmail(''); setSiPassword(''); setSiError(null); setBanReason(null); setSiLoading(false);
      setSuEmail(''); setSuPassword(''); setSuConfirm(''); setSuAccept(false);
      setSuError(null); setSuSuccess(false); setSuLoading(false); setSuCaptchaToken(null);
      setFpEmail(''); setFpError(null); setFpSuccess(false); setFpLoading(false);
    }
  }, [open, defaultTab]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSiError(null); setBanReason(null);

    // Brute-force throttle (client-side; Supabase has server-side too).
    const lockSec = getLockoutSecondsLeft(siEmail);
    if (lockSec > 0) {
      const mins = Math.ceil(lockSec / 60);
      setSiError(`För många misslyckade försök. Försök igen om ~${mins} min.`);
      return;
    }

    setSiLoading(true);
    const result = await signIn(siEmail, siPassword);
    if (result.error?.startsWith('BANNED:')) {
      setBanReason(result.error.slice('BANNED:'.length) || null);
    } else if (result.error) {
      const newLock = recordSignInFailure(siEmail);
      if (newLock > 0) {
        const mins = Math.ceil(newLock / 60);
        setSiError(`För många misslyckade försök. Kontot pausas i ~${mins} min.`);
      } else {
        setSiError(result.error);
      }
    } else {
      clearSignInThrottle(siEmail);
      onOpenChange(false);
    }
    setSiLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuError(null);
    if (suPassword !== suConfirm) {
      setSuError('Lösenorden matchar inte.');
      return;
    }
    if (!suAccept) {
      setSuError('Du måste godkänna villkoren.');
      return;
    }
    if (TURNSTILE_ENABLED && !suCaptchaToken) {
      setSuError('Lös CAPTCHA-utmaningen.');
      return;
    }
    setSuLoading(true);
    const result = await signUp(suEmail, suPassword);
    if (result.error) {
      setSuError(result.error);
    } else {
      setSuSuccess(true);
    }
    setSuLoading(false);
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setFpError(null);
    setFpLoading(true);
    const result = await resetPassword(fpEmail);
    if (result.error) {
      setFpError(result.error);
    } else {
      setFpSuccess(true);
    }
    setFpLoading(false);
  };

  const strength = scorePassword(suPassword);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Välkommen</DialogTitle>
          <DialogDescription>
            Logga in eller skapa ett konto för att synka arbetsytor.
          </DialogDescription>
        </DialogHeader>

        {/* Ban-block has highest precedence — replaces the whole form. */}
        {banReason !== null ? (
          <div className="space-y-3 rounded-md border border-destructive/40 bg-destructive/10 p-4">
            <div className="flex items-center gap-2 text-destructive font-semibold">
              <Ban className="w-5 h-5" />
              <span>You have been banned</span>
            </div>
            {banReason && (
              <div className="text-sm text-destructive/90">
                <span className="font-medium">Reason: </span>{banReason}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Om du tror att detta är ett misstag, kontakta administratören.
            </p>
            <Button variant="outline" className="w-full" onClick={() => setBanReason(null)}>
              Stäng
            </Button>
          </div>
        ) : (
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="signin" data-testid="login-tab-signin">
                <LogIn className="w-3.5 h-3.5 mr-1" /> Logga in
              </TabsTrigger>
              <TabsTrigger value="signup" data-testid="login-tab-signup">
                <UserPlus className="w-3.5 h-3.5 mr-1" /> Skapa
              </TabsTrigger>
              <TabsTrigger value="forgot" data-testid="login-tab-forgot">
                <KeyRound className="w-3.5 h-3.5 mr-1" /> Glömt
              </TabsTrigger>
            </TabsList>

            {/* ─── Sign in ─── */}
            <TabsContent value="signin" className="mt-3">
              <form onSubmit={handleSignIn} className="space-y-3">
                {siError && (
                  <div className="flex items-start gap-2 p-2.5 rounded-md bg-destructive/10 text-destructive text-xs">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{siError}</span>
                  </div>
                )}
                <div className="space-y-1">
                  <Label htmlFor="signin-email">E-post</Label>
                  <Input id="signin-email" type="email" value={siEmail}
                    onChange={(e) => setSiEmail(e.target.value)}
                    placeholder="namn@foretag.se" required autoComplete="email"
                    data-testid="signin-email" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="signin-password">Lösenord</Label>
                  <Input id="signin-password" type="password" value={siPassword}
                    onChange={(e) => setSiPassword(e.target.value)}
                    placeholder="••••••••" required minLength={6}
                    autoComplete="current-password" data-testid="signin-password" />
                </div>
                <Button type="submit" className="w-full" disabled={siLoading} data-testid="signin-submit">
                  {siLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Logga in
                </Button>
                <button type="button"
                  onClick={() => setTab('forgot')}
                  className="text-xs text-muted-foreground hover:underline w-full text-center">
                  Glömt lösenordet?
                </button>
                <OAuthButtons />
              </form>
            </TabsContent>

            {/* ─── Sign up ─── */}
            <TabsContent value="signup" className="mt-3">
              {suSuccess ? (
                <div className="space-y-3 rounded-md border border-emerald-400/40 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 text-sm">
                  <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold">
                    <CheckCircle2 className="w-5 h-5" />
                    Kontot är skapat
                  </div>
                  <p className="text-xs">
                    Vi har skickat ett bekräftelse-mejl till <strong>{suEmail}</strong>. Klicka på länken i mejlet för att aktivera kontot. Kolla även skräppost.
                  </p>
                  <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
                    Stäng
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSignUp} className="space-y-3">
                  {suError && (
                    <div className="flex items-start gap-2 p-2.5 rounded-md bg-destructive/10 text-destructive text-xs">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{suError}</span>
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label htmlFor="signup-email">E-post</Label>
                    <Input id="signup-email" type="email" value={suEmail}
                      onChange={(e) => setSuEmail(e.target.value)}
                      placeholder="namn@foretag.se" required autoComplete="email"
                      data-testid="signup-email" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="signup-password">Välj lösenord</Label>
                    <Input id="signup-password" type="password" value={suPassword}
                      onChange={(e) => setSuPassword(e.target.value)}
                      placeholder="Minst 8 tecken" required minLength={8}
                      autoComplete="new-password" data-testid="signup-password" />
                    {suPassword.length > 0 && (
                      <div className="flex items-center gap-2 text-[10px]">
                        <div className="flex-1 h-1 rounded-full bg-muted overflow-hidden">
                          <div
                            className={
                              strength.tone === 'destructive' ? 'h-full bg-destructive' :
                              strength.tone === 'amber' ? 'h-full bg-amber-500' :
                              'h-full bg-emerald-600'
                            }
                            style={{ width: `${(strength.score / 4) * 100}%` }}
                            data-testid="signup-password-strength"
                            data-score={strength.score}
                          />
                        </div>
                        <span className={
                          strength.tone === 'destructive' ? 'text-destructive font-medium' :
                          strength.tone === 'amber' ? 'text-amber-600 font-medium' :
                          'text-emerald-700 font-medium'
                        }>
                          {strength.label}
                        </span>
                      </div>
                    )}
                    {suPassword.length > 0 && (
                      <p className="text-[10px] text-muted-foreground">{strength.hint}</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="signup-confirm">Bekräfta lösenord</Label>
                    <Input id="signup-confirm" type="password" value={suConfirm}
                      onChange={(e) => setSuConfirm(e.target.value)}
                      required minLength={8} autoComplete="new-password"
                      data-testid="signup-confirm" />
                    {suConfirm.length > 0 && suPassword !== suConfirm && (
                      <p className="text-[10px] text-destructive">Lösenorden matchar inte.</p>
                    )}
                  </div>
                  <div className="flex items-start gap-2 text-xs">
                    <Checkbox id="signup-accept" checked={suAccept}
                      onCheckedChange={(v) => setSuAccept(v === true)}
                      data-testid="signup-accept" />
                    <Label htmlFor="signup-accept" className="text-xs leading-tight font-normal cursor-pointer">
                      Jag godkänner{' '}
                      <Link to="/terms" target="_blank" className="underline">Användarvillkoren</Link>
                      {' '}och{' '}
                      <Link to="/privacy" target="_blank" className="underline">Integritetspolicyn</Link>.
                    </Label>
                  </div>
                  <TurnstileWidget onChange={setSuCaptchaToken} />
                  <Button type="submit" className="w-full"
                    disabled={
                      suLoading || strength.score === 0 || !suAccept ||
                      suPassword !== suConfirm ||
                      (TURNSTILE_ENABLED && !suCaptchaToken)
                    }
                    data-testid="signup-submit">
                    {suLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Skapa konto
                  </Button>
                </form>
              )}
            </TabsContent>

            {/* ─── Forgot password ─── */}
            <TabsContent value="forgot" className="mt-3">
              {fpSuccess ? (
                <div className="space-y-3 rounded-md border border-emerald-400/40 bg-emerald-50/50 dark:bg-emerald-950/20 p-4 text-sm">
                  <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 font-semibold">
                    <CheckCircle2 className="w-5 h-5" />
                    Återställnings-länk skickad
                  </div>
                  <p className="text-xs">
                    Vi har skickat en länk till <strong>{fpEmail}</strong>. Klicka på länken för att välja ett nytt lösenord. Länken är giltig i 1 timme.
                  </p>
                  <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
                    Stäng
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleForgot} className="space-y-3">
                  {fpError && (
                    <div className="flex items-start gap-2 p-2.5 rounded-md bg-destructive/10 text-destructive text-xs">
                      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>{fpError}</span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Ange din e-postadress så skickar vi en länk för att återställa lösenordet.
                    Om kontot finns kommer mejlet inom 1-2 minuter.
                  </p>
                  <div className="space-y-1">
                    <Label htmlFor="forgot-email">E-post</Label>
                    <Input id="forgot-email" type="email" value={fpEmail}
                      onChange={(e) => setFpEmail(e.target.value)}
                      placeholder="namn@foretag.se" required autoComplete="email"
                      data-testid="forgot-email" />
                  </div>
                  <Button type="submit" className="w-full" disabled={fpLoading}
                    data-testid="forgot-submit">
                    {fpLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Skicka återställnings-länk
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
